import { AccountDetails } from '@twiliointernal/org/twilio/e2-commons/shared/definitions';

import { constants, types } from '../definitions';
import { IdConsole } from '../../../clients';
import logger from '../../../../lib/logger';

const determineAccountSwitchChanges = async ({
  account,
  accounts,
  newSid,
  options,
  subaccountsMetadata,
}: types.DetermineAccountSwitchChangesArgs): Promise<types.DetermineAccountSwitchChangesReturn> => {
  try {
    /**
     * The subaccount API needs to be used in two instances:
     * - Switching TO a subaccount
     * - Switching FROM a subaccount
     *
     * The latter case proves for some hairy logic below...
     */
    const isSwitchingToOrFromSubaccount = !!options?.isSubaccount;
    const response = isSwitchingToOrFromSubaccount
      ? await IdConsole.switchSubAccount(newSid)
      : await IdConsole.switchAccount(newSid, {});

    // A missing `projectSid` indicates an error from the accounts API
    if (!isSwitchingToOrFromSubaccount && !response.projectSid) {
      throw new Error('Account sid missing');
    }

    /**
     * Due to different API services, we set the currentAccountSid differently:
     * - Account: User projectSid from response
     * - Subaccount: No sid in response, use newSid from selection
     */
    const inComingAccountSid = isSwitchingToOrFromSubaccount
      ? newSid
      : response.projectSid;

    /**
     * TODO: Refine global state logic and/or update API to have a consolidated user identity
     * Check the incomingAccountSid to determine if we're switching
     * to a parent account:
     * - If yes: Use the matching account to update state values
     * - If no: No matching account is found, so we use a matching
     *   subaccount.
     *
     * The second condition is where this gets tricky because we need
     * to update the `account` in state to reflect the `sid/friendlyName`
     * of the subaccount, but cast the type to match `AccountDetails`.
     *
     * This is not a perfect solution, but suffices for our current needs.
     */
    const matchingAccount = accounts?.find(
      (account) => account.sid === inComingAccountSid,
    );
    const accountToUpdateState =
      matchingAccount ||
      subaccountsMetadata?.content?.find(
        (subaccount) => subaccount.sid === inComingAccountSid,
      );

    const incomingAccount = (
      matchingAccount
        ? matchingAccount
        : {
            ...account,
            sid: accountToUpdateState?.sid,
            friendlyName: accountToUpdateState?.friendlyName,
            parentAccountSid: account.sid,
          }
    ) as AccountDetails;

    return {
      incomingAccount,
      isSwitchingToSubaccount: !matchingAccount,
    };
  } catch (err) {
    logger.error(`${constants.LOG_PREFIX}::[updateCurrentAccountSid]:::${err}`);

    throw err;
  }
};

export default determineAccountSwitchChanges;
